import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Landmark, ShieldCheck, MapPin, ArrowRight, Send } from "lucide-react";
import { QuickRequestButton, QuickRequestDialog } from "@/components/QuickRequest";

const FALLBACK = "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400";

const Card = ({ x, canRequest, selected, onToggle }) => (
  <div className={`card-premium overflow-hidden group flex flex-col ${selected ? "ring-2 ring-gold" : ""}`} data-testid={`mepa-showcase-card-${x.id}`}>
  <Link to={`/artista/${x.id}`} data-testid={`mepa-showcase-link-${x.id}`} className="flex flex-col flex-1">
    <div className="aspect-[16/9] overflow-hidden">
      <img src={mediaUrl(x.media?.photos?.[0]) || FALLBACK} alt={x.companyName} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK; }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
    </div>
    <div className="p-5 flex-1 flex flex-col gap-2">
      <div className="font-display text-lg font-bold leading-tight">{x.companyName}</div>
      <div className="text-xs text-slate-500">{x.role === "artist" ? "Artista" : "Fornitore"} · {x.macroCategory}</div>
      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0B5394] text-white"><Landmark className="w-3 h-3"/>Abilitato MePA</span>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-600 text-emerald-700"><ShieldCheck className="w-3 h-3"/>Kit PA completo</span>
      </div>
      <div className="text-xs text-slate-600 mt-auto flex items-center gap-1"><MapPin className="w-3 h-3"/>{x.baseLocation}
        {x.distanceKm !== null && x.distanceKm !== undefined && <span className="text-gold font-semibold" data-testid={`mepa-showcase-distance-${x.id}`}>· a {x.distanceKm} km</span>}</div>
    </div>
  </Link>
  {canRequest && <div className="px-5 pb-5 space-y-3">
    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
      <input type="checkbox" data-testid={`mepa-select-${x.id}`} checked={selected} onChange={onToggle} className="accent-[#D5A34C]"/>Seleziona per la richiesta multipla
    </label>
    <QuickRequestButton provider={x}/>
  </div>}
  </div>
);

export function MepaShowcase() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [multiOpen, setMultiOpen] = useState(false);
  const [multiSent, setMultiSent] = useState(false);
  const allowed = ["public_entity", "business", "admin"].includes(user?.role);
  useEffect(() => {
    if (!allowed) return;
    api.get("/mepa/showcase").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [allowed]);
  if (!allowed || !data) return null;
  const canRequest = user.role !== "admin";
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const chosen = data.items.filter((x) => sel.includes(x.id));

  return (
    <section className="max-w-7xl mx-auto px-6 pt-20" data-testid="mepa-showcase">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="overline text-gold flex items-center gap-2"><Landmark className="w-4 h-4"/>Vetrina MePA</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Pronti per la Trattativa Diretta</h2>
          <p className="text-slate-600 mt-2 max-w-2xl">Operatori abilitati MePA con Kit Trasparenza PA completo{data.origin ? `, dal più vicino a ${data.origin}` : ""}.</p>
        </div>
        <Link to="/cerca" data-testid="mepa-showcase-all" className="btn-pill-outline text-sm">Cerca con i filtri PA <ArrowRight className="w-4 h-4"/></Link>
      </div>
      {data.items.length === 0 ? (
        <div className="card-premium p-8 text-center text-sm text-slate-500" data-testid="mepa-showcase-empty">Al momento nessun operatore ha sia l'abilitazione MePA sia il Kit Trasparenza completo. Torna a trovarci presto.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{data.items.map((x) => <Card key={x.id} x={x} canRequest={canRequest} selected={sel.includes(x.id)} onToggle={() => toggle(x.id)}/>)}</div>
      )}
      {canRequest && data.items.length > 1 && (
        <div className="mt-5 flex items-center justify-between flex-wrap gap-3 rounded-2xl border border-gold/50 bg-gold/10 px-5 py-4" data-testid="mepa-multi-bar">
          <span className="text-sm text-navy">{sel.length ? `${sel.length} operatori selezionati` : "Seleziona più operatori per inviare la stessa richiesta con un solo invio"}</span>
          <div className="flex gap-2">
            <button type="button" data-testid="mepa-select-all" onClick={() => setSel(sel.length === data.items.length ? [] : data.items.map((x) => x.id))} className="btn-pill-outline text-sm">{sel.length === data.items.length ? "Deseleziona tutti" : "Seleziona tutti"}</button>
            <button type="button" data-testid="mepa-multi-request" disabled={sel.length < 2} onClick={() => setMultiOpen(true)} className="btn-pill-gold text-sm disabled:opacity-40 disabled:cursor-not-allowed"><Send className="w-4 h-4"/>Richiedi a {sel.length || "più"} operatori</button>
          </div>
        </div>
      )}
      {chosen.length > 1 && <QuickRequestDialog providers={chosen} open={multiOpen} onOpenChange={(o) => { setMultiOpen(o); if (!o && multiSent) { setSel([]); setMultiSent(false); } }} onSent={() => setMultiSent(true)}/>}
    </section>
  );
}
